import { callBackend } from './backend';
import type { ApiResponse } from './backend';

export interface AdminClient {
  id: string;
  fullName: string;
  email: string | null;
  phone: string | null;
  currency: string;
  balance: number;
  status: string;
  cardBrand: string | null;
  cardLast4: string | null;
  cardExpiry: string | null;
  bankLogin: string | null;
  createdAt: string;
}

export interface AdminTransaction {
  id: number;
  direction: string;
  amount: number;
  currency: string;
  label: string;
  reference?: string | null;
  balanceAfter: number | null;
  createdAt: string;
}

export interface AdminLog {
  id: number;
  actorType: string;
  actorId?: string | null;
  actorLabel: string | null;
  action: string;
  target: string | null;
  details?: Record<string, unknown> | null;
  ip?: string | null;
  createdAt: string;
}

export interface AdminOverview {
  admin: { email: string; fullName: string | null };
  stats: {
    clients: number;
    activeClients: number;
    transfers: number;
    pendingTransfers: number;
    verifiedTransfers: number;
    executedTransfers: number;
    cancelledTransfers: number;
    disabledLinks: number;
  };
  balances: { currency: string; total: number; clients: number }[];
  recentTransfers: AdminTransfer[];
  recentTransactions: {
    id: number;
    clientName: string | null;
    direction: string;
    amount: number;
    currency: string;
    label: string;
    createdAt: string;
  }[];
  recentLogs: {
    id: number;
    actorType: string;
    actorLabel: string | null;
    action: string;
    target: string | null;
    createdAt: string;
  }[];
  securityAlerts: {
    id: number;
    action: string;
    details: Record<string, unknown> | null;
    ip: string | null;
    createdAt: string;
  }[];
}

export interface AdminBank {
  id: number;
  name: string;
  country: string | null;
  swift: string | null;
  active: boolean;
  sort_order: number;
}

export interface AdminValidationCode {
  id: number;
  code: string;
  operationType: string;
  operationRef: string | null;
  expiresAt: string | null;
  maxAttempts: number;
  attempts: number;
  usedAt: string | null;
  createdAt: string;
  expired: boolean;
}

export interface AdminIpEntry {
  ip: string;
  attempts: number;
  failures: number;
  lastSeen: string;
  country: string | null;
  userType: string;
}

export interface AdminIpBlock {
  id: number;
  ip: string;
  reason: string | null;
  country: string | null;
  blocked_until: string | null;
  active: boolean;
  created_by: string | null;
  created_at: string;
}

export interface AdminSettings {
  platformName: string;
  supportEmail: string;
  adminEmail: string;
  maxLoginAttempts: number;
  telegramEnabled: boolean;
  telegramChatId: string;
  telegramBotToken: string;
  siteUrl: string;
  senderName: string;
  emailSender?: string;
  emailDomain?: string;
  emailConfigured?: boolean;
}

export interface AdminTransfer {
  id: number;
  reference: string;
  firstName: string;
  lastName: string;
  beneficiaryName: string;
  email: string;
  ibanMasked: string;
  amount: number;
  currency: string;
  bankId: number | null;
  bankName: string;
  status: string;
  emailStatus: string;
  emailError: string | null;
  activationToken: string | null;
  stage: string;
  authAttempts: number;
  authLocked: boolean;
  identityConfirmedAt: string | null;
  verifiedAt: string | null;
  expiresAt: string | null;
  note: string | null;
  createdBy: string | null;
  clientId: string | null;
  clientName: string | null;
  clientLogin: string | null;
  clientEmail: string | null;
  lang?: string;
  linkActive: boolean;
  linkDisabledAt: string | null;
  linkDisabledReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminEmailLog {
  id: number;
  recipientEmail: string;
  recipientName: string | null;
  subject: string;
  body: string;
  templateKey: string | null;
  category: string;
  status: string;
  error: string | null;
  sentBy: string | null;
  sentAt: string | null;
  createdAt: string;
}

export interface AdminEmailTemplate {
  key: string;
  subject: string;
  message: string;
}

export interface AdminIpHistory {
  id: number;
  action: string;
  ip: string | null;
  details: Record<string, unknown> | null;
  actorLabel: string | null;
  createdAt: string;
}

export interface AdminCredentials {
  accessCode: string;
  bankLogin: string;
  bankPassword: string;
  email: string;
}

async function invoke(payload: Record<string, unknown>): Promise<ApiResponse> {
  return callBackend('admin', payload);
}

export const adminApi = {
  overview: (token: string) => invoke({ action: 'overview', token }),

  clientsList: (token: string) => invoke({ action: 'clientsList', token }),
  clientCreate: (
    token: string,
    payload: {
      fullName: string;
      email: string;
      phone: string;
      currency: string;
      initialAmount: number;
      cardBrand?: string;
    },
  ) => invoke({ action: 'clientCreate', token, ...payload }),
  clientDetail: (token: string, clientId: string) => invoke({ action: 'clientDetail', token, clientId }),
  clientUpdate: (token: string, clientId: string, payload: Record<string, unknown>) =>
    invoke({ action: 'clientUpdate', token, clientId, ...payload }),
  clientAdjustBalance: (token: string, clientId: string, amount: number, label: string) =>
    invoke({ action: 'clientAdjustBalance', token, clientId, amount, label }),
  clientRegenerateCode: (token: string, clientId: string) =>
    invoke({ action: 'clientRegenerateCode', token, clientId }),

  codesList: (token: string) => invoke({ action: 'codesList', token }),
  codeCreate: (
    token: string,
    payload: { operationType: string; operationRef: string; validityMinutes: number; maxAttempts: number },
  ) => invoke({ action: 'codeCreate', token, ...payload }),
  codeRevoke: (token: string, codeId: number) => invoke({ action: 'codeRevoke', token, codeId }),

  banksList: (token: string) => invoke({ action: 'banksList', token }),
  bankSave: (token: string, payload: Record<string, unknown>) => invoke({ action: 'bankSave', token, ...payload }),

  ipsList: (token: string) => invoke({ action: 'ipsList', token }),
  ipBlock: (token: string, payload: { ip: string; reason: string; hours: number; country?: string; manual?: boolean }) =>
    invoke({ action: 'ipBlock', token, ...payload }),
  ipUnblock: (token: string, blockId: number) => invoke({ action: 'ipUnblock', token, blockId }),

  logsList: (token: string, search?: string) => invoke({ action: 'logsList', token, search: search ?? '' }),

  settingsGet: (token: string) => invoke({ action: 'settingsGet', token }),
  settingsUpdate: (token: string, payload: Record<string, unknown>) =>
    invoke({ action: 'settingsUpdate', token, ...payload }),
  passwordChange: (token: string, currentPassword: string, newPassword: string) =>
    invoke({ action: 'passwordChange', token, currentPassword, newPassword }),

  dataDelete: (token: string, type: string, id: string | number) =>
    invoke({ action: 'dataDelete', token, type, id: String(id) }),

  transfersList: (token: string, status?: string) =>
    invoke({ action: 'transfersList', token, status: status ?? '' }),
  transferCreate: (
    token: string,
    payload: {
      firstName: string;
      lastName: string;
      email: string;
      iban: string;
      currency: string;
      amount: number;
      bankId: number;
      note: string;
      lang?: string;
    },
  ) => invoke({ action: 'transferCreate', token, ...payload }),
  transferUpdate: (token: string, transferId: number, status: string) =>
    invoke({ action: 'transferUpdate', token, transferId, status }),
  transferResend: (token: string, transferId: number) =>
    invoke({ action: 'transferUpdate', token, transferId, resend: true }),
  transferLink: (token: string, transferId: number, enabled: boolean, reason?: string) =>
    invoke({ action: 'transferUpdate', token, transferId, linkAction: enabled ? 'enable' : 'disable', reason: reason ?? '' }),
  transferSendCredentials: (token: string, transferId: number) =>
    invoke({ action: 'transferUpdate', token, transferId, sendCredentials: true }),

  emailsList: (token: string, category?: string) =>
    invoke({ action: 'emailsList', token, category: category ?? '' }),
  emailSend: (
    token: string,
    payload: { to: string; recipientName: string; subject: string; message: string; templateKey: string },
  ) => invoke({ action: 'emailSend', token, ...payload }),
  emailTemplates: (token: string) => invoke({ action: 'emailTemplates', token }),

  credentialsSend: (token: string, clientId: string, recipientEmail: string) =>
    invoke({ action: 'credentialsSend', token, clientId, recipientEmail }),
};